import React from 'react';
import { Form, Input, Button, Select, Spin, Card, Result, message, Table, Alert, Modal } from 'antd';
import axios from 'axios';
import moment from 'moment';
import { Link } from 'react-router-dom';
import * as serverconfig from '../serverconn';
import { DownloadOutlined, LoadingOutlined } from '@ant-design/icons';
import {
  getOnboardingUploadTypes,
  getOperationalUploadTypes,
  getTemplateConfig,
} from '../dataUploadTemplates';
import { downloadUploadTemplate, formatDataSize, parseUploadFile } from '../dataUploadUtils';

var CryptoJS = require('crypto-js');

const FormItem = Form.Item;
const { Option } = Select;
const dateFormat = 'DD/MM/YYYY';
const antIcon = <LoadingOutlined style={{ fontSize: 60 }} spin />;

var token = '';
var sacco = '';
var username = '';

class DataUploadForm extends React.Component {
  state = {
    date: moment().format(dateFormat).toString(),
    excelfilefile: null,
    saccoboject: {},
    datalist: [],
    uploadtype: '',
    columns: [],
    message: '',
    companyprofile: {},
    datarequested: false,
    datasubmittedsuccessfully: false,
    parseStats: null,
    headerMismatches: [],
  };

  componentDidMount() {
    if (localStorage.getItem('sacco')) {
      sacco = CryptoJS.AES.decrypt(localStorage.getItem('sacco'), 'my-secret-key@12345').toString(CryptoJS.enc.Utf8);
      username = CryptoJS.AES.decrypt(localStorage.getItem('username'), 'my-secret-key@123').toString(CryptoJS.enc.Utf8);
    } else {
      sacco = '';
      username = '';
    }

    token = localStorage.getItem('token') || '';

    axios.defaults.headers = {
      'Content-Type': 'application/json',
      Authorization: `Token ${token}`,
    };

    axios.get(`${serverconfig.backendserverurl}/api/saccos/${sacco}`).then((res) => {
      this.setState({
        saccoboject: res.data,
        companyprofile: res.data,
        datarequested: false,
      });
    });
  }

  handleUploadTypeChange = (val) => {
    this.setState({
      uploadtype: val,
      datalist: [],
      columns: [],
      excelfilefile: null,
      parseStats: null,
      headerMismatches: [],
    });
  };

  handleDownloadTemplate = () => {
    if (!this.state.uploadtype) {
      message.warning('Select an upload type first.');
      return;
    }
    const ok = downloadUploadTemplate(this.state.uploadtype);
    if (ok) {
      message.success('Template downloaded with Instructions and Template sheets.');
    } else {
      message.error('Could not generate template for this upload type.');
    }
  };

  handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) {
      return;
    }

    if (!this.state.uploadtype) {
      message.warning('Select an upload type before uploading a file.');
      e.target.value = '';
      return;
    }

    this.setState({
      datarequested: true,
      excelfilefile: selectedFile,
      datalist: [],
      columns: [],
      parseStats: null,
      headerMismatches: [],
    });

    parseUploadFile(selectedFile, this.state.uploadtype)
      .then((result) => {
        if (!result.ok) {
          message.error(result.error);
          this.setState({
            datarequested: false,
            headerMismatches: result.headerMismatches || [],
          });
          return;
        }

        const { stats } = result;
        if (result.datalist.length === 0) {
          message.warning('No valid rows to import. Review the validation summary below.');
        } else {
          message.success(`Ready to import ${result.datalist.length} row(s).`);
        }

        if (stats.skippedSampleRows > 0) {
          message.info(`Skipped ${stats.skippedSampleRows} sample row(s) from the template.`);
        }

        this.setState({
          datalist: result.datalist,
          columns: result.columns,
          parseStats: stats,
          datarequested: false,
        });
      })
      .catch((err) => {
        message.error(err.message || 'Failed to process the uploaded file.');
        this.setState({ datarequested: false });
      });
  };

  submitUpload = () => {
    this.setState({ datarequested: true });

    const form_data = new FormData();
    form_data.append('sacco', sacco);
    form_data.append('datalist', JSON.stringify(this.state.datalist));
    form_data.append('username', username);
    form_data.append('uploadtype', this.state.uploadtype);

    axios
      .post(`${serverconfig.backendserverurl}/customqueries/final_dataupload`, form_data, {
        headers: { 'content-type': 'multipart/form-data' },
      })
      .then((res) => {
        this.setState({
          message: res.data.message,
          datarequested: false,
          datasubmittedsuccessfully: true,
        });
      })
      .catch((err) => {
        const errMsg = err.response?.data?.message || 'Upload failed. Please try again.';
        message.error(errMsg);
        this.setState({ datarequested: false });
      });
  };

  handleFormSubmit = () => {
    if (this.state.datalist.length <= 0) {
      message.error('No valid rows to upload.');
      return;
    }

    const dataSizeBytes = JSON.stringify(this.state.datalist).length;
    const templateConfig = getTemplateConfig(this.state.uploadtype);
    const isBackground = templateConfig && !['chartofaccounts', 'loanopeningbalances', 'special_journal_entries'].includes(this.state.uploadtype);

    Modal.confirm({
      title: 'Confirm data import',
      content: (
        <div>
          <p>Upload type: <strong>{templateConfig?.label}</strong></p>
          <p>Rows to import: <strong>{this.state.datalist.length}</strong></p>
          <p>Payload size: <strong>{formatDataSize(dataSizeBytes)}</strong></p>
          {isBackground && <p>This import runs in the background. You can continue using the system while it processes.</p>}
        </div>
      ),
      okText: 'Upload',
      cancelText: 'Cancel',
      onOk: () => this.submitUpload(),
    });
  };

  renderParseSummary() {
    const { parseStats } = this.state;
    if (!parseStats) {
      return null;
    }

    return (
      <Alert
        type={parseStats.importedRows > 0 ? 'success' : 'warning'}
        showIcon
        style={{ marginBottom: 16 }}
        message="Import preview summary"
        description={(
          <div>
            <div>Total rows in file: {parseStats.totalRows}</div>
            <div>Rows ready to import: {parseStats.importedRows}</div>
            {parseStats.skippedSampleRows > 0 && <div>Sample rows skipped: {parseStats.skippedSampleRows}</div>}
            {parseStats.skippedInvalidRows > 0 && (
              <div>
                Invalid rows skipped: {parseStats.skippedInvalidRows}
                <ul style={{ marginTop: 8, marginBottom: 0 }}>
                  {parseStats.invalidRows.slice(0, 5).map((item) => (
                    <li key={`invalid-${item.rowNumber}`}>
                      Row {item.rowNumber}: {item.issues.join('; ')}
                    </li>
                  ))}
                  {parseStats.invalidRows.length > 5 && (
                    <li>...and {parseStats.invalidRows.length - 5} more</li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}
      />
    );
  }

  renderUploadTypeOptions() {
    const onboardingDone = this.state.companyprofile.client_onboarding_done === true;
    const options = onboardingDone ? [] : getOnboardingUploadTypes();
    options.push(...getOperationalUploadTypes());

    return options.map((item) => (
      <Option key={item.value} value={item.value}>
        {item.label}
      </Option>
    ));
  }

  render() {
    if (this.state.datarequested === true) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Spin indicator={antIcon} />
        </div>
      );
    }

    if (this.state.datasubmittedsuccessfully === true) {
      return (
        <Card>
          <Result
            status="success"
            title="Successful"
            subTitle={this.state.message}
            extra={[
              <Button type="primary" key="finish" onClick={() => window.location.reload(false)}>
                Finish
              </Button>,
            ]}
          />
        </Card>
      );
    }

    const templateConfig = getTemplateConfig(this.state.uploadtype);

    return (
      <div>
        <h3>Data upload section</h3>
        <Alert
          type="warning"
          showIcon
          message="Download the template first. Do not change column headings. Row 2 is a sample and will be auto-skipped."
          style={{ marginBottom: 16 }}
        />
        <Alert
          type="info"
          showIcon
          message={(
            <span>
              Need member-specific JSON imports (journals, statements, loan workflow)?
              {' '}
              <Link to="/dataonboarding_json/">Use the JSON import tool</Link>
              {' '}
              | Loan opening balances also available on{' '}
              <Link to="/loanopening_balances_upload/">dedicated page</Link>
            </span>
          )}
          style={{ marginBottom: 16 }}
        />
        <Card>
          <Form layout="vertical">
            <FormItem label="Upload type">
              <Select
                value={this.state.uploadtype || undefined}
                onChange={this.handleUploadTypeChange}
                placeholder="Upload type"
                style={{ width: '100%' }}
              >
                {this.renderUploadTypeOptions()}
              </Select>
            </FormItem>

            {templateConfig && (
              <Alert type="info" showIcon message={templateConfig.hint} style={{ marginBottom: 16 }} />
            )}

            {this.state.headerMismatches.length > 0 && (
              <Alert
                type="error"
                showIcon
                message="Column heading mismatch"
                description={(
                  <ul style={{ marginBottom: 0 }}>
                    {this.state.headerMismatches.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                )}
                style={{ marginBottom: 16 }}
              />
            )}

            <FormItem label="Template">
              <Button
                icon={<DownloadOutlined />}
                disabled={!this.state.uploadtype}
                onClick={this.handleDownloadTemplate}
              >
                Download Excel template
              </Button>
              {templateConfig && (
                <div style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
                  Columns: {templateConfig.headers.join(', ')}
                </div>
              )}
            </FormItem>

            <FormItem label="Excel data file">
              <Input type="file" accept=".xlsx,.xls" onChange={this.handleFileChange} />
            </FormItem>
          </Form>
        </Card>

        <br />
        {this.renderParseSummary()}
        {this.state.datalist.length > 0 && (
          <div>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: 12 }}>
              <h2 style={{ margin: 0 }}>Processed Data ({this.state.datalist.length} rows)</h2>
              &nbsp;&nbsp;
              <Button type="primary" onClick={this.handleFormSubmit}>
                Upload data
              </Button>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <Table
                dataSource={this.state.datalist}
                columns={this.state.columns}
                scroll={{ x: 1000 }}
                bordered
                pagination={{ showQuickJumper: true, showSizeChanger: true }}
                size="small"
                rowKey={(record, index) => String(index)}
              />
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default DataUploadForm;
